import { P, Predicate } from "breadcommon";
import dayjs from "dayjs";

export interface TransactionColumnFilterDisplayDateProps {
  predicate: Predicate;
}

export function TransactionColumnFilterDisplayDate({
  predicate,
}: TransactionColumnFilterDisplayDateProps): JSX.Element {
  // Parse the predicate to extract start/end dates
  // Expected structure: P.All(P.GreaterThanEqualTo(P.ToUnixTimestamp(...), P.Literal(unixtime)), P.LessThanEqualTo(...))
  const serializable = predicate.asSerializable();

  let startUnix: number | null = null;
  let endUnix: number | null = null;

  if (serializable.name === P.OpName.All && serializable.args.length > 0) {
    // Multiple conditions in P.All
    for (const arg of serializable.args) {
      if (arg.name === P.OpName.GreaterThanEqualTo && arg.args.length === 2) {
        const literalOp = arg.args[1];
        if (
          literalOp.name === P.OpName.Literal &&
          typeof literalOp.args[0] === "number"
        ) {
          startUnix = literalOp.args[0];
        }
      } else if (
        arg.name === P.OpName.LessThanEqualTo &&
        arg.args.length === 2
      ) {
        const literalOp = arg.args[1];
        if (
          literalOp.name === P.OpName.Literal &&
          typeof literalOp.args[0] === "number"
        ) {
          endUnix = literalOp.args[0];
        }
      }
    }
  } else if (
    serializable.name === P.OpName.GreaterThanEqualTo &&
    serializable.args.length === 2
  ) {
    // Single start date condition
    const literalOp = serializable.args[1];
    if (
      literalOp.name === P.OpName.Literal &&
      typeof literalOp.args[0] === "number"
    ) {
      startUnix = literalOp.args[0];
    }
  } else if (
    serializable.name === P.OpName.LessThanEqualTo &&
    serializable.args.length === 2
  ) {
    // Single end date condition
    const literalOp = serializable.args[1];
    if (
      literalOp.name === P.OpName.Literal &&
      typeof literalOp.args[0] === "number"
    ) {
      endUnix = literalOp.args[0];
    }
  }

  // Format dates nicely
  const formatDate = (unixTime: number): string => {
    return dayjs.unix(unixTime).format("MMM YYYY");
  };

  if (startUnix !== null && endUnix !== null) {
    const startDate = formatDate(startUnix);
    const endDate = formatDate(endUnix);
    if (startDate === endDate) {
      return <span>{startDate}</span>;
    } else {
      return (
        <span>
          {startDate} through {endDate}
        </span>
      );
    }
  } else if (startUnix !== null) {
    return <span>{formatDate(startUnix)} and later</span>;
  } else if (endUnix !== null) {
    return <span>Up through {formatDate(endUnix)}</span>;
  }

  // Fallback if predicate structure is unexpected
  return <span>Date filter</span>;
}
